import {
  MapDataFieldRefGuestValueWithMetadata,
  PickedMeetingLocation,
} from '@chilipiper/api-client/src/chat-platform-chat'
import * as v from 'valibot'
import {
  MeetingAttemptResponse,
  MeetingAttendees,
} from '@chilipiper/api-client/src/user-availability'
import { MeetingAttendees as MeetingAttendeesRequest } from '@chilipiper/api-client/src/team-scheduling'
import {
  MapLocaleGuestFormLocalization,
  MeetingLocation,
  QuestionDataType,
} from '@chilipiper/api-client/src/meeting-types'
import { DataFieldDataType } from '@chilipiper/api-client/src/chili-crm'
import { GuestFormValues } from '@chilipiper/booking-flow'

/**
 * Transforms a MeetingLocation to a PickedMeetingLocation with all required fields. This ensures
 * that location types requiring a host field (like ZoomOneTimeLink) are properly handled.
 */
export const transformMeetingLocation = (location: MeetingLocation): PickedMeetingLocation => {
  if (
    location.type === 'ZoomOneTimeLink' ||
    location.type === 'WebexLink' ||
    location.type === 'GongLink' ||
    location.type === 'GoToMeetingLink' ||
    location.type === 'HostsDefaultConferenceDetails' ||
    location.type === 'HostsDefaultPhysicalLocation' ||
    location.type === 'TeamsMeetingLink' ||
    location.type === 'RingCentralLink'
  ) {
    return {
      type: location.type,
      host: location.host,
    }
  }

  if (location.type === 'DefinedInMeetingType') {
    return {
      type: location.type,
      value: location.value,
    }
  }

  return {
    type: location.type,
  }
}

export const mapQuestionDataTypeToDataFieldDataType = (
  questionDataType: QuestionDataType
): DataFieldDataType => {
  switch (questionDataType.type) {
    case 'QuestionCheckboxes':
      return {
        type: 'Checkbox',
        values: questionDataType.values,
      }
    case 'QuestionEmail':
      return {
        type: 'Email',
      }

    case 'QuestionMultiLineText':
      return { type: 'LongText' }
    case 'QuestionPhone':
      return {
        type: 'Phone',
      }
    case 'QuestionPicklist':
      return { type: 'Picklist', values: questionDataType.values }
    case 'QuestionRadioButtons':
      return { type: 'RadioButton', values: questionDataType.values }
    case 'QuestionSingleLineText':
      return { type: 'ShortText' }
    case 'QuestionTrueFalse':
      return { type: 'TrueFalse' }
  }
}

export const mapMeetingAttendees = (attendees?: MeetingAttendees): MeetingAttendeesRequest => {
  if (!attendees) {
    return {
      type: 'NoAdditionalAttendees',
    }
  }
  return attendees.type === 'SomeAttendees'
    ? {
        ...attendees,
        attendees: attendees.attendees.map(attendee => ({
          ...attendee,
          responseStatus: 'NeedsAction',
        })),
      }
    : attendees
}

export const ErrorSchema = v.object({
  error: v.object({
    errorCode: v.string(),
    errorMessage: v.string(),
  }),
})

/**
 * Extracts the correct meeting type ID from various sources.
 *
 * For RoundRobinSchedulingLink, OwnershipSchedulingLink, GroupSchedulingLink cases, this returns
 * the selectedMeetingTypeId. For other cases, returns the original block meeting type ID.
 *
 * Note: The useMeetingTypeResolution hook handles cases where the backend sends scheduling link IDs
 * instead of meeting type IDs.
 *
 * @param blockMeetingType - The meeting type ID from the block payload
 * @param meetingAttempt - Optional meeting attempt data with product feature info
 * @returns The resolved meeting type ID
 */
export const getMeetingTypeId = (
  blockMeetingType: string,
  meetingAttempt?: MeetingAttemptResponse
) => {
  if (
    (meetingAttempt?.type === 'Available' || meetingAttempt?.type === 'Booked') &&
    (meetingAttempt?.attempt.productFeature.type === 'RoundRobinSchedulingLink' ||
      meetingAttempt?.attempt.productFeature.type === 'OwnershipSchedulingLink' ||
      meetingAttempt?.attempt.productFeature.type === 'GroupSchedulingLink')
  ) {
    const selectedMeetingTypeId = meetingAttempt.attempt.productFeature.selectedMeetingTypeId
    if (selectedMeetingTypeId) {
      return selectedMeetingTypeId
    }
  }

  return blockMeetingType
}

export const getFieldText = (fieldLocalization: MapLocaleGuestFormLocalization) => {
  if (fieldLocalization['en-US']) {
    return fieldLocalization['en-US']
  }
  return (
    Object.entries(fieldLocalization).find(([key, value]) => {
      if (key.startsWith('en')) {
        return value
      }
    })?.[1] ?? Object.values(fieldLocalization)[0]
  )
}

export const updateGuestFormData = (
  currentGuestData: GuestFormValues,
  previousGuestData: MapDataFieldRefGuestValueWithMetadata
) => {
  // Emit only new/changed fields compared to existing guest state
  const diff: Record<string, unknown> = {}
  Object.entries(currentGuestData).forEach(([key, value]) => {
    const normalized = Array.isArray(value) ? value[0] : value
    if (normalized !== undefined && normalized !== null) {
      // compare primitive/stringified values to catch shallow changes
      const prevGuestDataVal = previousGuestData[key]
      const prevGuestDataStr =
        typeof prevGuestDataVal === 'object'
          ? JSON.stringify(prevGuestDataVal)
          : String(prevGuestDataVal ?? '')
      const currGuestDataStr =
        typeof normalized === 'object' ? JSON.stringify(normalized) : String(normalized)
      if (prevGuestDataStr !== currGuestDataStr) {
        diff[key] = normalized
      }
    }
  })
  return diff
}
